/**
 * The phrase tables for the live compare-condition helper — exact strings
 * from the copy-panel spec. Strictness convention: strict ops (< >) say
 * "more than / by any amount / still under"; inclusive ops (<= >=) say
 * "{delta}% or more / at most / at least". Banned words: scale, factor,
 * multiplier, ratio, threshold — the copy speaks trader English only.
 * For Media Devoted | Created By Ciro Autiero
 */

/** Interpolation tokens, pre-formatted by the caller. */
export interface CompareTokens {
  lhs: string;
  /** `{rhs} over {window}` — the different-metric reference. */
  R: string;
  /** `its {window} level` — the same-metric reference. */
  windowLevel: string;
  /** Percent as typed, thousands-separated. */
  p: string;
  /** |100 − p|, trimmed. */
  delta: string;
  /** p/100, trimmed — only used at p ≥ 200. */
  x: string;
}

export type CompareOp = '<' | '<=' | '=' | '!=' | '>' | '>=';
export type OrderedOp = '<' | '<=' | '>' | '>=';
export type PercentBand = 'low' | 'even' | 'high' | 'multi';

/** Line 1 when NO percent is active — the plain, direct comparison. */
export function plainSummary(op: OrderedOp, t: CompareTokens, ref: string): string {
  const phrase: Record<OrderedOp, string> = {
    '<': 'is below',
    '<=': 'is at or below',
    '>': 'is above',
    '>=': 'is at or above',
  };
  return `Matches when ${t.lhs} ${phrase[op]} ${ref}.`;
}

type Phrase = (t: CompareTokens) => string;
type BandTable = Record<OrderedOp, Record<PercentBand, Phrase>>;

/** Line 1 — SAME metric both sides (named window). */
export const SAME_METRIC: BandTable = {
  '<': {
    low: (t) => `Matches when ${t.lhs} has dropped more than ${t.delta}% below ${t.windowLevel}.`,
    even: (t) => `Matches when ${t.lhs} is below ${t.windowLevel}, by any amount — no tolerance.`,
    high: (t) => `Matches when ${t.lhs} is still under ${t.p}% of ${t.windowLevel} — it hasn't gained ${t.delta}% on it.`,
    multi: (t) => `Matches when ${t.lhs} is still under ${t.x}× ${t.windowLevel}.`,
  },
  '<=': {
    low: (t) => `Matches when ${t.lhs} has dropped ${t.delta}% or more below ${t.windowLevel}.`,
    even: (t) => `Matches when ${t.lhs} is at or below ${t.windowLevel} — no tolerance.`,
    high: (t) => `Matches when ${t.lhs} is at most ${t.p}% of ${t.windowLevel} — gained ${t.delta}% or less on it.`,
    multi: (t) => `Matches when ${t.lhs} is at most ${t.x}× ${t.windowLevel}.`,
  },
  '>': {
    low: (t) => `Matches when ${t.lhs} is holding above ${t.p}% of ${t.windowLevel} — down less than ${t.delta}%, if at all.`,
    even: (t) => `Matches when ${t.lhs} is above ${t.windowLevel}, by any amount — no tolerance.`,
    high: (t) => `Matches when ${t.lhs} is running more than ${t.delta}% above ${t.windowLevel}.`,
    multi: (t) => `Matches when ${t.lhs} is running above ${t.x}× ${t.windowLevel}.`,
  },
  '>=': {
    low: (t) => `Matches when ${t.lhs} is at ${t.p}% of ${t.windowLevel} or better — down at most ${t.delta}%.`,
    even: (t) => `Matches when ${t.lhs} is at or above ${t.windowLevel} — no tolerance.`,
    high: (t) => `Matches when ${t.lhs} is running at least ${t.delta}% above ${t.windowLevel}.`,
    multi: (t) => `Matches when ${t.lhs} is at least ${t.x}× ${t.windowLevel}.`,
  },
};

/** Line 1 — DIFFERENT metrics. */
export const DIFF_METRIC: BandTable = {
  '<': {
    low: (t) => `Matches when ${t.lhs} is more than ${t.delta}% lower than ${t.R}.`,
    even: (t) => `Matches when ${t.lhs} is below ${t.R}, by any amount — no tolerance.`,
    high: (t) => `Matches when ${t.lhs} is under ${t.p}% of ${t.R} — not ${t.delta}% ahead of it yet.`,
    multi: (t) => `Matches when ${t.lhs} is under ${t.x}× ${t.R}.`,
  },
  '<=': {
    low: (t) => `Matches when ${t.lhs} is ${t.delta}% or more lower than ${t.R}.`,
    even: (t) => `Matches when ${t.lhs} is at or below ${t.R} — no tolerance.`,
    high: (t) => `Matches when ${t.lhs} is at most ${t.p}% of ${t.R}.`,
    multi: (t) => `Matches when ${t.lhs} is at most ${t.x}× ${t.R}.`,
  },
  '>': {
    low: (t) => `Matches when ${t.lhs} is above ${t.p}% of ${t.R}.`,
    even: (t) => `Matches when ${t.lhs} beats ${t.R}, by any amount — no tolerance.`,
    high: (t) => `Matches when ${t.lhs} beats ${t.R} by more than ${t.delta}%.`,
    multi: (t) => `Matches when ${t.lhs} is more than ${t.x}× ${t.R}.`,
  },
  '>=': {
    low: (t) => `Matches when ${t.lhs} is at least ${t.p}% of ${t.R}.`,
    even: (t) => `Matches when ${t.lhs} is at or above ${t.R} — no tolerance.`,
    high: (t) => `Matches when ${t.lhs} beats ${t.R} by ${t.delta}% or more.`,
    multi: (t) => `Matches when ${t.lhs} is at least ${t.x}× ${t.R}.`,
  },
};

/** Line 1 for `=` / `!=` — honest about exact ties on live numbers. */
export function equalitySummary(
  op: '=' | '!=',
  band: PercentBand,
  t: CompareTokens,
  ref: string,
): string {
  const target =
    band === 'even' ? ref : band === 'multi' ? `${t.x}× ${ref}` : `${t.p}% of ${ref}`;
  if (op === '=') {
    const lands = band === 'even' ? `exactly equals ${target}` : `lands exactly on ${target}`;
    const rare = band === 'even' ? 'exact ties are rare' : 'live numbers almost never do';
    return `Matches only when ${t.lhs} ${lands} — ${rare} on live numbers; ≤ or ≥ is usually what you want.`;
  }
  if (band === 'even') {
    return `Matches whenever ${t.lhs} differs at all from ${target} — with live numbers that's almost always.`;
  }
  return `Matches unless ${t.lhs} is exactly ${target} — with live numbers that's almost always.`;
}

/** Line 2 op phrase with the number inline; `short` = same-metric wording. */
export function exampleOpPhrase(op: string, p: string, short: boolean): string {
  const table: Record<string, [string, string]> = {
    '<': [`is below ${p}`, `below ${p}`],
    '<=': [`is ${p} or lower`, `${p} or lower`],
    '>': [`is above ${p}`, `above ${p}`],
    '>=': [`is ${p} or higher`, `${p} or higher`],
    '=': [`is exactly ${p}`, `exactly ${p}`],
    '!=': [`is anything but ${p}`, `anything but ${p}`],
  };
  const [full, brief] = table[op] ?? [`is ${op} ${p}`, `${op} ${p}`];
  return short ? brief : full;
}
